// src/components/admin/CommunityManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const API_BASE = process.env.REACT_APP_API_URL;
if (!API_BASE) {
  throw new Error('API URL not configured');
}

const CommunityManagement = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentMember, setCurrentMember] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, countries: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    region: '',
    membership_type: 'Professional',
    role: 'Member',
    status: 'Active',
    registration_date: new Date().toISOString().split('T')[0],
    renewal_date: '',
    organization: '',
    profession: '',
    notes: ''
  });

  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    recipients: 'all', // 'all', 'active', 'selected'
    selectedMembers: []
  });

  const membershipTypes = ['Professional', 'Student', 'Associate', 'Honorary', 'Corporate'];
  const roles = ['Member', 'Volunteer', 'Coordinator', 'Director', 'Administrator'];
  const statuses = ['Active', 'Inactive', 'Pending', 'Suspended'];

  useEffect(() => {
    fetchMembers();
    fetchStats();
  }, [searchTerm, filterType, filterStatus, filterCountry, pagination.page]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      let url = `${API_BASE}/admin/community?page=${pagination.page}&limit=${pagination.limit}&`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterType) url += `membership_type=${filterType}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterCountry) url += `country=${encodeURIComponent(filterCountry)}&`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setMembers(data.data.members || []);
        setPagination(prev => ({
          ...prev,
          total: data.data.total || 0,
          totalPages: data.data.totalPages || 0
        }));
      } else {
        toast.error(data.message || 'Failed to fetch members');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to fetch members');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/community/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentMember(null);
    setFormData({
      full_name: '', email: '', phone: '', country: '', region: '',
      membership_type: 'Professional', role: 'Member', status: 'Active',
      registration_date: new Date().toISOString().split('T')[0],
      renewal_date: '', organization: '', profession: '', notes: ''
    });
    setShowModal(true);
  };

  const handleEdit = (member) => {
    setModalMode('edit');
    setCurrentMember(member);
    setFormData({
      full_name: member.full_name || '',
      email: member.email || '',
      phone: member.phone || '',
      country: member.country || '',
      region: member.region || '',
      membership_type: member.membership_type || 'Professional',
      role: member.role || 'Member',
      status: member.status || 'Active',
      registration_date: member.registration_date ? member.registration_date.substring(0, 10) : '',
      renewal_date: member.renewal_date ? member.renewal_date.substring(0, 10) : '',
      organization: member.organization || '',
      profession: member.profession || '',
      notes: member.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this member?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/community/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchMembers();
        fetchStats();
        toast.success('Member deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete member');
      }
    } catch (err) {
      toast.error('Failed to delete member');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/community`
        : `${API_BASE}/admin/community/${currentMember.id}`;

      const response = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchMembers();
        fetchStats();
        toast.success(`Member ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/admin/community/send-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });

      const data = await response.json();
      if (data.success) {
        setShowMessageModal(false);
        toast.success('Message sent successfully');
        setMessageData({ subject: '', message: '', recipients: 'all', selectedMembers: [] });
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMessageInputChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({ ...prev, [name]: value }));
  };

  if (loading && members.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status"></div>
        <span className="ms-3 text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: '#1f2937' }}>Community Management</h2>
          <p className="text-muted mb-0">Manage ARCA community members and volunteers</p>
        </div>
        <div className="d-flex gap-2">
          <button
            onClick={() => setShowMessageModal(true)}
            className="btn btn-sm shadow-sm"
            style={{ backgroundColor: '#2196F3', color: 'white' }}
          >
            <i className="bi bi-envelope me-2"></i>Send Announcement
          </button>
          <button
            onClick={handleCreate}
            className="btn btn-sm fw-bold shadow-sm"
            style={{ backgroundColor: '#6B8E23', color: 'white' }}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Member
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
            <div className="card-body">
              <small className="text-muted">Total Members</small>
              <h4 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #4CAF50' }}>
            <div className="card-body">
              <small className="text-muted">Active</small>
              <h4 className="fw-bold mb-0" style={{ color: '#4CAF50' }}>{stats.active || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FFC107' }}>
            <div className="card-body">
              <small className="text-muted">Pending</small>
              <h4 className="fw-bold mb-0" style={{ color: '#FFC107' }}>{stats.pending || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FF9800' }}>
            <div className="card-body">
              <small className="text-muted">Countries</small>
              <h4 className="fw-bold mb-0" style={{ color: '#FF9800' }}>{stats.countries || 0}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <input type="text" className="form-control" placeholder="Search members..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterType} onChange={(e) => { setFilterType(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                <option value="">All Types</option>
                {membershipTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
                <option value="">All Statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <input type="text" className="form-control" placeholder="Filter by country..." value={filterCountry} onChange={(e) => { setFilterCountry(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="px-4 py-3">Member</th>
                <th className="py-3">Type</th>
                <th className="py-3">Role</th>
                <th className="py-3">Country</th>
                <th className="py-3">Status</th>
                <th className="py-3">Renewal</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-5 text-muted">No members found</td></tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td className="px-4 py-3">
                      <div>
                        <div className="fw-medium">{member.full_name}</div>
                        <small className="text-muted">{member.email}</small>
                      </div>
                    </td>
                    <td className="py-3"><span className="badge" style={{ backgroundColor: '#6B8E2320', color: '#6B8E23' }}>{member.membership_type}</span></td>
                    <td className="py-3">{member.role}</td>
                    <td className="py-3">{member.country || '—'}</td>
                    <td className="py-3">
                      <span className={`badge ${member.status === 'Active' ? 'bg-success' : member.status === 'Pending' ? 'bg-warning' : 'bg-secondary'}`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="py-3">{member.renewal_date ? new Date(member.renewal_date).toLocaleDateString() : '—'}</td>
                    <td className="py-3 text-center">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          onClick={() => handleEdit(member)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2196F3', color: 'white', border: 'none' }}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', marginLeft: '4px' }}
                        >
                          <i className="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <nav className="mt-3">
          <ul className="pagination justify-content-center flex-wrap">
            <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>Previous</button>
            </li>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
              <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPagination(p => ({ ...p, page }))}>{page}</button>
              </li>
            ))}
            <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      {/* Member Form Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">{modalMode === 'create' ? 'Add Member' : 'Edit Member'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Full Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="full_name" value={formData.full_name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email <span className="text-danger">*</span></label>
                      <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Phone</label>
                      <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <input type="text" className="form-control" name="country" value={formData.country} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Region</label>
                      <input type="text" className="form-control" name="region" value={formData.region} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Membership Type</label>
                      <select className="form-select" name="membership_type" value={formData.membership_type} onChange={handleInputChange}>
                        {membershipTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Role</label>
                      <select className="form-select" name="role" value={formData.role} onChange={handleInputChange}>
                        {roles.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <select className="form-select" name="status" value={formData.status} onChange={handleInputChange}>
                        {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Registration Date</label>
                      <input type="date" className="form-control" name="registration_date" value={formData.registration_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Renewal Date</label>
                      <input type="date" className="form-control" name="renewal_date" value={formData.renewal_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Organization</label>
                      <input type="text" className="form-control" name="organization" value={formData.organization} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Profession</label>
                      <input type="text" className="form-control" name="profession" value={formData.profession} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notes</label>
                      <textarea className="form-control" name="notes" value={formData.notes} onChange={handleInputChange} rows="3"></textarea>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ backgroundColor: '#6B8E23', color: 'white' }} onClick={handleSubmit}>
                  {modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#2196F3', color: 'white' }}>
                <h5 className="modal-title">Send Announcement</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowMessageModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSendMessage}>
                  <div className="mb-3">
                    <label className="form-label">Recipients</label>
                    <select className="form-select" name="recipients" value={messageData.recipients} onChange={handleMessageInputChange}>
                      <option value="all">All Members</option>
                      <option value="active">Active Members Only</option>
                      <option value="pending">Pending Members Only</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Subject <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="subject" value={messageData.subject} onChange={handleMessageInputChange} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Message <span className="text-danger">*</span></label>
                    <textarea className="form-control" name="message" value={messageData.message} onChange={handleMessageInputChange} rows="6" required></textarea>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowMessageModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ backgroundColor: '#2196F3', color: 'white' }} onClick={handleSendMessage}>
                  <i className="bi bi-send me-2"></i>Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityManagement;